import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, Sparkles, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("admin@serenityskin.com");
    setPassword("Serenity@2026");
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#FAF8F5] px-4" data-testid="admin-login-page">
      <div aria-hidden="true" className="animate-drift pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-[#F3E5AB]/40 blur-3xl" />
      <div aria-hidden="true" className="animate-drift-slow pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-[#E8D5CE]/50 blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="glass relative w-full max-w-md rounded-[2rem] p-8 shadow-[0_20px_60px_rgba(212,175,55,0.12)] sm:p-10"
      >
        <div className="flex items-center gap-3">
          <img src="/assets/logo-mark.png" alt="Royal Skin Cosmetic Clinic logo" className="h-12 w-auto object-contain" />
          <span className="leading-none">
            <span className="font-serif text-xl font-semibold text-[#1A1615]">Royal Skin</span>
            <span className="block text-[9px] font-medium tracking-[0.3em] text-[#B38B2D]">BACKOFFICE</span>
          </span>
        </div>
        <h1 className="mt-8 font-serif text-3xl font-semibold text-[#1A1615]">Welcome back</h1>
        <p className="mt-2 text-sm font-light text-[#5A504B]">Sign in to manage your clinic's website content and enquiries.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label htmlFor="admin-email" className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@serenityskin.com"
              data-testid="admin-login-email-input"
              className="input-lux"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-login-password-input"
              className="input-lux"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </p>
          )}
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="btn-gold w-full px-6 py-3.5 text-sm disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Lock className="h-4 w-4" aria-hidden="true" />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
          <button
            type="button"
            onClick={fillDemo}
            data-testid="admin-demo-fill-button"
            className="flex w-full items-center justify-center gap-2 text-xs font-medium text-[#B38B2D] transition-colors hover:text-[#8a6d1f]"
          >
            <Wand2 className="h-3.5 w-3.5" aria-hidden="true" /> Auto-fill demo credentials
          </button>
        </form>
        <p className="mt-8 text-center text-xs text-[#A39288]">
          <Link to="/" data-testid="admin-back-to-site-link" className="transition-colors hover:text-[#B38B2D]">
            ← Back to website
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
